import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  InputWrapper,
  Loader,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useGetHealthcareProfessionalsByFacility } from '../../../global/entities/healthcareProfessional/queries/useGetHealthcareProfessionalsByFacility.tsx';
import { useGetEscalationConfigForFacility } from '../../../global/entities/EICFTemplates/queries/useGetEscalationConfigForFacility.tsx';
import { usePutEscalationConfigForFacility } from '../../../global/entities/EICFTemplates/mutations/usePutEscalationConfigForFacility.tsx';
import { useGetAllowedSignersForFacility } from '../../../global/entities/EICFTemplates/queries/useGetAllowedSignersForFacility.tsx';
import { usePutAllowedSignersForFacility } from '../../../global/entities/EICFTemplates/mutations/usePutAllowedSignersForFacility.tsx';
import { IEICFEscalationConfig } from '../../../global/entities/EICFTemplates/EICFTemplate.ts';
import HcpMultiSelect from '../components/HcpMultiSelect';

interface HcfEICFEscalationPanelProps {
  facilityId: number;
}

type DelayUnit = 'minutes' | 'hours' | 'days';

const emptyConfig: IEICFEscalationConfig = {
  initialNotifyByEmail: true,
  initialNotifyBySMS: false,
  initialHcpIds: [],
  level1Enabled: false,
  level1DelayMinutes: null,
  level1NotifyByEmail: true,
  level1NotifyBySMS: false,
  level1HcpIds: [],
  level2Enabled: false,
  level2DelayMinutes: null,
  level2NotifyByEmail: true,
  level2NotifyBySMS: false,
  level2HcpIds: [],
};

const unitFromMinutes = (minutes: number | null): DelayUnit => {
  if (!minutes) return 'days';
  if (minutes % 1440 === 0) return 'days';
  if (minutes % 60 === 0) return 'hours';
  return 'minutes';
};

const displayFromMinutes = (minutes: number | null, unit: DelayUnit): number => {
  if (!minutes) return 0;
  if (unit === 'days') return minutes / 1440;
  if (unit === 'hours') return minutes / 60;
  return minutes;
};

const toMinutes = (value: number, unit: DelayUnit): number => {
  if (unit === 'days') return Math.round(value * 1440);
  if (unit === 'hours') return Math.round(value * 60);
  return Math.round(value);
};

const HcfEICFEscalationPanel: React.FC<HcfEICFEscalationPanelProps> = ({
  facilityId,
}) => {
  const { t } = useTranslation();
  const { data: hcps, isLoading: isLoadingHcps } =
    useGetHealthcareProfessionalsByFacility(facilityId);
  const { data: allowedIds, isLoading: isLoadingAllowed } =
    useGetAllowedSignersForFacility(facilityId);
  const { data: config, isLoading: isLoadingConfig } =
    useGetEscalationConfigForFacility(facilityId);
  const putAllowed = usePutAllowedSignersForFacility();
  const putConfig = usePutEscalationConfigForFacility();

  const [selectedSigners, setSelectedSigners] = useState<number[]>([]);
  const [draft, setDraft] = useState<IEICFEscalationConfig>(emptyConfig);
  const [level1Unit, setLevel1Unit] = useState<DelayUnit>('days');
  const [level2Unit, setLevel2Unit] = useState<DelayUnit>('days');

  useEffect(() => {
    setSelectedSigners(allowedIds ?? []);
  }, [allowedIds]);

  useEffect(() => {
    if (config) {
      setDraft(config);
      setLevel1Unit(unitFromMinutes(config.level1DelayMinutes));
      setLevel2Unit(unitFromMinutes(config.level2DelayMinutes));
    }
  }, [config]);

  const update = (patch: Partial<IEICFEscalationConfig>) =>
    setDraft((current) => ({ ...current, ...patch }));

  // Notification recipients can only be allowed signers. When a signer is removed
  // from the allowed list, drop them from every notification list immediately.
  const handleSignersChange = (ids: number[]) => {
    setSelectedSigners(ids);
    setDraft((current) => ({
      ...current,
      initialHcpIds: current.initialHcpIds.filter((id) => ids.includes(id)),
      level1HcpIds: current.level1HcpIds.filter((id) => ids.includes(id)),
      level2HcpIds: current.level2HcpIds.filter((id) => ids.includes(id)),
    }));
  };

  const handleSave = async () => {
    // Save allowed signers first so the recipient invariant is enforced against the
    // up-to-date allowed set when the escalation config is persisted.
    await putAllowed.mutateAsync({ facilityId, hcpIds: selectedSigners });
    await putConfig.mutateAsync({ facilityId, config: draft });
  };

  const unitData = [
    { value: 'minutes', label: t('eicf.admin.escalationUnitMinutes') },
    { value: 'hours', label: t('eicf.admin.escalationUnitHours') },
    { value: 'days', label: t('eicf.admin.escalationUnitDays') },
  ];

  const allowedHcps = (hcps ?? []).filter((h) =>
    selectedSigners.includes(h.id),
  );

  const isLoading = isLoadingHcps || isLoadingAllowed || isLoadingConfig;
  const noSigners = selectedSigners.length === 0;

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="sm">
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <Stack gap="xs">
              <div>
                <Text fw={600}>{t('eicf.admin.allowedSignersLabel')}</Text>
                <Text size="sm" c="dimmed">
                  {t('eicf.admin.allowedSignersHelp')}
                </Text>
              </div>
              <InputWrapper>
                <HcpMultiSelect
                  value={selectedSigners}
                  onChange={handleSignersChange}
                  hcpsData={hcps ?? []}
                  placeholder={t('eicf.admin.selectAllowedSigners')}
                />
              </InputWrapper>
              {noSigners && (
                <Alert
                  color="yellow"
                  icon={<IconAlertCircle size="1rem" />}
                  variant="light"
                >
                  {t('eicf.admin.allowedSignersEmptyWarning')}
                </Alert>
              )}
            </Stack>

            <Divider />

            <Stack gap="xs">
              <div>
                <Text fw={600}>{t('eicf.admin.initialNotificationLabel')}</Text>
                <Text size="sm" c="dimmed">
                  {t('eicf.admin.initialNotificationHelp')}
                </Text>
              </div>
              <InputWrapper label={t('eicf.admin.initialRecipients')}>
                <HcpMultiSelect
                  value={draft.initialHcpIds}
                  onChange={(ids) => update({ initialHcpIds: ids })}
                  hcpsData={allowedHcps}
                  placeholder={t('eicf.admin.selectEscalationHcps')}
                />
              </InputWrapper>
              <Group>
                <Checkbox
                  label={t('eicf.admin.escalationNotifyByEmail')}
                  checked={draft.initialNotifyByEmail}
                  onChange={(e) =>
                    update({ initialNotifyByEmail: e.currentTarget.checked })
                  }
                />
                <Checkbox
                  label={t('eicf.admin.escalationNotifyBySMS')}
                  checked={draft.initialNotifyBySMS}
                  onChange={(e) =>
                    update({ initialNotifyBySMS: e.currentTarget.checked })
                  }
                />
              </Group>
            </Stack>

            <Divider />

            <div>
              <Text fw={600}>{t('eicf.admin.escalationLabel')}</Text>
              <Text size="sm" c="dimmed">
                {t('eicf.admin.escalationHelp')}
              </Text>
            </div>

            <Switch
              label={t('eicf.admin.escalationLevel1')}
              checked={draft.level1Enabled}
              onChange={(e) => {
                const checked = e.currentTarget.checked;
                if (checked) {
                  update({
                    level1Enabled: true,
                    level1DelayMinutes: draft.level1DelayMinutes ?? 1440,
                  });
                  if (!draft.level1DelayMinutes) setLevel1Unit('days');
                } else {
                  // Disabling Level 1 clears its delay/unit (so re-enabling defaults to Days)
                  // and clears Level 2 (it cannot exist without Level 1).
                  setLevel1Unit('days');
                  setLevel2Unit('days');
                  update({
                    level1Enabled: false,
                    level1DelayMinutes: null,
                    level2Enabled: false,
                    level2DelayMinutes: null,
                    level2NotifyByEmail: true,
                    level2NotifyBySMS: false,
                    level2HcpIds: [],
                  });
                }
              }}
            />

            {draft.level1Enabled && (
              <Stack gap="xs">
                <Group grow>
                  <NumberInput
                    label={t('eicf.admin.escalationDelayLevel1')}
                    min={1}
                    value={displayFromMinutes(draft.level1DelayMinutes, level1Unit)}
                    onChange={(v) =>
                      update({
                        level1DelayMinutes: toMinutes(
                          typeof v === 'number' ? v : 0,
                          level1Unit,
                        ),
                      })
                    }
                  />
                  <Select
                    label={t('eicf.admin.escalationUnit')}
                    data={unitData}
                    value={level1Unit}
                    allowDeselect={false}
                    onChange={(v) => {
                      const unit = (v as DelayUnit) || 'days';
                      const current = displayFromMinutes(
                        draft.level1DelayMinutes,
                        level1Unit,
                      );
                      setLevel1Unit(unit);
                      update({ level1DelayMinutes: toMinutes(current, unit) });
                    }}
                  />
                </Group>
                <InputWrapper label={t('eicf.admin.escalationRecipients')}>
                  <HcpMultiSelect
                    value={draft.level1HcpIds}
                    onChange={(ids) => update({ level1HcpIds: ids })}
                    hcpsData={allowedHcps}
                    placeholder={t('eicf.admin.selectEscalationHcps')}
                  />
                </InputWrapper>
                <Group>
                  <Checkbox
                    label={t('eicf.admin.escalationNotifyByEmail')}
                    checked={draft.level1NotifyByEmail}
                    onChange={(e) =>
                      update({ level1NotifyByEmail: e.currentTarget.checked })
                    }
                  />
                  <Checkbox
                    label={t('eicf.admin.escalationNotifyBySMS')}
                    checked={draft.level1NotifyBySMS}
                    onChange={(e) =>
                      update({ level1NotifyBySMS: e.currentTarget.checked })
                    }
                  />
                </Group>

                <Divider variant="dashed" />

                <Switch
                  label={t('eicf.admin.escalationLevel2')}
                  checked={draft.level2Enabled}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    if (checked) {
                      update({
                        level2Enabled: true,
                        level2DelayMinutes: draft.level2DelayMinutes ?? 1440,
                      });
                      if (!draft.level2DelayMinutes) setLevel2Unit('days');
                    } else {
                      setLevel2Unit('days');
                      update({
                        level2Enabled: false,
                        level2DelayMinutes: null,
                        level2NotifyByEmail: true,
                        level2NotifyBySMS: false,
                        level2HcpIds: [],
                      });
                    }
                  }}
                />

                {draft.level2Enabled && (
                  <Stack gap="xs">
                    <Group grow>
                      <NumberInput
                        label={t('eicf.admin.escalationDelayLevel2')}
                        min={1}
                        value={displayFromMinutes(
                          draft.level2DelayMinutes,
                          level2Unit,
                        )}
                        onChange={(v) =>
                          update({
                            level2DelayMinutes: toMinutes(
                              typeof v === 'number' ? v : 0,
                              level2Unit,
                            ),
                          })
                        }
                      />
                      <Select
                        label={t('eicf.admin.escalationUnit')}
                        data={unitData}
                        value={level2Unit}
                        allowDeselect={false}
                        onChange={(v) => {
                          const unit = (v as DelayUnit) || 'days';
                          const current = displayFromMinutes(
                            draft.level2DelayMinutes,
                            level2Unit,
                          );
                          setLevel2Unit(unit);
                          update({ level2DelayMinutes: toMinutes(current, unit) });
                        }}
                      />
                    </Group>
                    <InputWrapper label={t('eicf.admin.escalationRecipients')}>
                      <HcpMultiSelect
                        value={draft.level2HcpIds}
                        onChange={(ids) => update({ level2HcpIds: ids })}
                        hcpsData={allowedHcps}
                        placeholder={t('eicf.admin.selectEscalationHcps')}
                      />
                    </InputWrapper>
                    <Group>
                      <Checkbox
                        label={t('eicf.admin.escalationNotifyByEmail')}
                        checked={draft.level2NotifyByEmail}
                        onChange={(e) =>
                          update({
                            level2NotifyByEmail: e.currentTarget.checked,
                          })
                        }
                      />
                      <Checkbox
                        label={t('eicf.admin.escalationNotifyBySMS')}
                        checked={draft.level2NotifyBySMS}
                        onChange={(e) =>
                          update({ level2NotifyBySMS: e.currentTarget.checked })
                        }
                      />
                    </Group>
                  </Stack>
                )}
              </Stack>
            )}

            <Group justify="flex-end">
              <Button
                onClick={handleSave}
                loading={putAllowed.isPending || putConfig.isPending}
              >
                {t('save')}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Card>
  );
};

export default HcfEICFEscalationPanel;
